import { Text, useToast, Box, Skeleton, VStack } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  finleyAssert,
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { typeguards, FinleyAi } from '@finley-technologies/finley-types';

import { useGetFinleyAiThreadsQuery } from '../requests/ai.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import {
  doesSearchQueryExistInString,
  SearchBar,
} from 'packages/common-frontend/components/SearchAndCopyComponents';
import { errorToastConfig } from 'packages/common-frontend/constants';

export const deserializeFinleyAiThread = (
  thread: Object,
): FinleyAi.FinleyAiThread => {
  finleyAssert(
    'created_at' in thread &&
      typeof thread.created_at === 'string' &&
      'updated_at' in thread &&
      typeof thread.updated_at === 'string',
    'Unable to load Finley Ai Thread.',
  );

  const finleyAiThread = {
    ...thread,
    created_at: new Date(thread.created_at),
    updated_at: new Date(thread.updated_at),
  };

  finleyAssert(
    typeguards.finleyAi.isFinleyAiThread(finleyAiThread),
    'Unable to load Finley Ai Thread.',
  );

  return finleyAiThread;
};

export const deserializeFinleyAiThreads = (
  threads: Object[],
): FinleyAi.FinleyAiThread[] => {
  const finleyAiThreads = threads.map((thread) =>
    deserializeFinleyAiThread(thread),
  );

  return finleyAiThreads;
};

export const deserializeFinleyAiThreadWithPromptResponses = (
  thread: Object,
): FinleyAi.FinleyAiThreadWithPromptResponses => {
  // we will rely on jsonschema to typeguard this
  // gql serializes JsDate to IsoString, we need to deserialize this
  // manually prior to passing to typeguard layer.
  finleyAssert(
    'created_at' in thread &&
      typeof thread.created_at === 'string' &&
      'updated_at' in thread &&
      typeof thread.updated_at === 'string' &&
      'promptResponses' in thread &&
      Array.isArray(thread.promptResponses),
    'Unable to load Finley Ai Threads.',
  );

  const finleyAiThreadWithPromptResponses = {
    ...thread,
    created_at: new Date(thread.created_at),
    updated_at: new Date(thread.updated_at),
    promptResponses: thread.promptResponses.map((pr) => ({
      ...pr,
      created_at: new Date(pr.created_at),
      updated_at: new Date(pr.updated_at),
    })),
  };

  finleyAssert(
    typeguards.finleyAi.isFinleyAiThreadWithPromptResponses(
      finleyAiThreadWithPromptResponses,
    ),
    'Unable to load Finley Ai Thread.',
  );

  return finleyAiThreadWithPromptResponses;
};

type FinleyAiThreadProps = {
  thread: FinleyAi.FinleyAiThread;
  handleFinleyAiThreadSelection: (thread: FinleyAi.FinleyAiThread) => void;
  isSelected: boolean;
};
export const FinleyAiThread = ({
  thread,
  handleFinleyAiThreadSelection,
  isSelected,
}: FinleyAiThreadProps) => {
  const { title } = thread;

  return (
    <Text
      backgroundColor={isSelected ? colors.gray[600] : undefined}
      onClick={() => handleFinleyAiThreadSelection(thread)}
    >
      {title ?? 'Untitled'}
    </Text>
  );
};

type FinleyAiThreadsBodyProps = {
  handleFinleyAiThreadSelection: (thread: FinleyAi.FinleyAiThread) => void;
  finleyAiThreadId: string | null;
  threads: FinleyAi.FinleyAiThread[];
};
const FinleyAiThreadsBody = ({
  handleFinleyAiThreadSelection,
  finleyAiThreadId,
  threads,
}: FinleyAiThreadsBodyProps) => {
  const [searchBarQuery, setSearchBarQuery] = useState<string>('');

  const filteredThreadsAfterSearch = useMemo(() => {
    // group by updated at date
    const groupedThreads = _.groupBy(threads, (thread) =>
      commonDate.dateToFullDateString(thread.updated_at),
    );

    const filteredThreads: {
      date: string;
      threads: FinleyAi.FinleyAiThread[];
    }[] = [];

    if (_.size(groupedThreads)) {
      _.forEach(groupedThreads, (threadGroup, date) => {
        finleyAssert(
          commonTypeguards.isFullDateStr(date),
          `Expected [date: ${date}] to be a FullDateStr`,
        );

        const filteredThreadsForDate: FinleyAi.FinleyAiThread[] = [];
        if (threadGroup.length) {
          threadGroup.forEach((thread) => {
            if (
              doesSearchQueryExistInString({
                queryString: searchBarQuery,
                stringToEvaluate: thread.title || '',
              })
            ) {
              filteredThreadsForDate.push(thread);
            }
          });
        }

        if (filteredThreadsForDate.length) {
          filteredThreads.push({
            date: commonDate.fullDateStrToHumanDateString(date),
            threads: filteredThreadsForDate,
          });
        }
      });
    }

    return filteredThreads;
  }, [searchBarQuery, threads]);

  const doThreadsExist = !!filteredThreadsAfterSearch.length;

  if (!doThreadsExist) {
    return (
      <Text color={colors.black[70]}>
        No history available. You can start a new chat.
      </Text>
    );
  }

  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      backgroundColor={colors.background[50]}
      overflow="auto"
      maxH="80vh"
    >
      <Box position="sticky" top="0">
        <SearchBar
          query={searchBarQuery}
          onChangeQuery={setSearchBarQuery}
          isDisabled={!doThreadsExist}
          backgroundColor={colors.background[50]}
        />
      </Box>

      {_.map(filteredThreadsAfterSearch, (threadGroupByDate) => {
        return (
          <Box
            key={`thread-group-${threadGroupByDate.date}`}
            cursor="default"
            px="16px"
            mb="16px"
          >
            <Text fontSize="14px" color={colors.black[70]} mb="16px">
              {threadGroupByDate.date}
            </Text>
            {threadGroupByDate.threads.map((thread) => {
              const isSelected = thread.id === finleyAiThreadId;
              return (
                <Text
                  cursor="pointer"
                  key={`thread-selection-${thread.id}`}
                  p="8px 16px"
                  borderRadius="8px"
                  color={colors.black[70]}
                  backgroundColor={isSelected ? colors.aurora[50] : undefined}
                  _hover={{ backgroundColor: colors.aurora[50] }}
                  onClick={() => handleFinleyAiThreadSelection(thread)}
                >
                  {thread.title}
                </Text>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const FinleyAiThreadsLoadingSkeleton = () => {
  return (
    <Box
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      backgroundColor={colors.background[50]}
      overflow="auto"
      h="100%"
    >
      <Box position="sticky" top="0">
        <SearchBar query="" onChangeQuery={() => {}} isDisabled />
      </Box>
      <VStack spacing="8px" alignItems="left" p="0 16px">
        {_.times(16, () => (
          <Skeleton h="30px" />
        ))}
      </VStack>
    </Box>
  );
};

type FinleyAiThreadsProps = {
  userId: string;
  handleFinleyAiThreadSelection: (thread: FinleyAi.FinleyAiThread) => void;
  finleyAiThreadId: string | null;
};
export const FinleyAiThreads = ({
  userId,
  handleFinleyAiThreadSelection,
  finleyAiThreadId,
}: FinleyAiThreadsProps) => {
  const toast = useToast();

  const { data: finleyAiThreadsData } = useGetFinleyAiThreadsQuery({
    variables: { userId },
    fetchPolicy: 'network-only',
    onError: () => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to load Finley Ai Threads.`,
      });
    },
  });

  const threads = useMemo(
    () =>
      deserializeFinleyAiThreads(
        finleyAiThreadsData?.getFinleyAiThreads?.threads ?? [],
      ),
    [finleyAiThreadsData],
  );

  if (!finleyAiThreadsData?.getFinleyAiThreads?.threads) {
    return <FinleyAiThreadsLoadingSkeleton />;
  }

  return (
    <FinleyAiThreadsBody
      threads={threads}
      handleFinleyAiThreadSelection={handleFinleyAiThreadSelection}
      finleyAiThreadId={finleyAiThreadId}
    />
  );
};
